/**
 * LexDuo · F3 Ejecuciones · Wizard nueva ejecución (3 pasos)
 * Paso 1: Selecciona expediente
 * Paso 2: Sube PDF resolución + análisis IA
 * Paso 3: Revisa + ajusta + crea
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem,
} from '../ui/command';
import {
  Check, ChevronsUpDown, Loader2, Save, Banknote, Hammer, Gavel,
  Upload, Sparkles, FileText, ArrowRight, ArrowLeft, AlertTriangle,
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { obtenerToken } from '../../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const headers = (extra = {}) => {
  const tok = obtenerToken();
  return { ...(tok ? { Authorization: `Bearer ${tok}` } : {}), ...extra };
};

const EjecucionForm = ({ open, onClose, onCreated, expedienteIdInicial = null }) => {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [ejecucionId, setEjecucionId] = useState(null); // borrador creado en paso 1
  const [analysis, setAnalysis] = useState(null);
  const [pdfFiles, setPdfFiles] = useState([]); // __V69_MULTI__
  const setPdfFile = (f) => setPdfFiles(f ? [f] : []);
  const pdfFile = pdfFiles[0] || null;

  // Datos del formulario (paso 3)
  const [expedienteId, setExpedienteId] = useState(expedienteIdInicial || null);
  const [tipo, setTipo] = useState('dineraria');
  const [hacerPersonalisimo, setHacerPersonalisimo] = useState(null);
  const [objeto, setObjeto] = useState('');
  const [importePrincipal, setImportePrincipal] = useState('');
  const [importeIntereses, setImporteIntereses] = useState('');
  const [importeCostas, setImporteCostas] = useState('');
  const [fechaResolucion, setFechaResolucion] = useState('');
  const [fechaFirmeza, setFechaFirmeza] = useState('');
  const [notas, setNotas] = useState('');

  // Combobox expedientes
  const [openExpCmb, setOpenExpCmb] = useState(false);
  const [expedienteQuery, setExpedienteQuery] = useState('');
  const [expedientesOpts, setExpedientesOpts] = useState([]);
  const [loadingExp, setLoadingExp] = useState(false);
  const [selectedExp, setSelectedExp] = useState(null);

  const buscarExpedientes = async (q) => {
    setLoadingExp(true);
    try {
      // Backend espera ?search=&limit= (ver routers/expedientes.py get_expedientes)
      const params = q?.trim() ? { search: q.trim(), limit: 50 } : { limit: 50 };
      const r = await axios.get(`${BACKEND_URL}/api/expedientes`, { params, headers: headers() });
      // Backend devuelve un array plano (response_model=List[ExpedienteResponse])
      const arr = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.expedientes || r.data?.data || []);
      setExpedientesOpts(Array.isArray(arr) ? arr : []);
    } catch (e) { console.warn('Error buscando expedientes:', e); setExpedientesOpts([]); }
    finally { setLoadingExp(false); }
  };

  useEffect(() => { if (open) buscarExpedientes(''); }, [open]);
  useEffect(() => {
    const t = setTimeout(() => { if (open) buscarExpedientes(expedienteQuery); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [expedienteQuery]);

  // ── Paso 1 → Paso 2 ──
  const goStep2 = async () => {
    if (!expedienteId) {
      toast({ title: 'Selecciona un expediente', variant: 'destructive' }); return;
    }
    setBusy(true);
    try {
      // Crear borrador inicial con expediente_id + tipo provisional
      const r = await axios.post(`${BACKEND_URL}/api/ejecuciones`,
        { expediente_id: parseInt(expedienteId, 10), tipo: 'dineraria' },
        { headers: headers() });
      setEjecucionId(r.data.id);
      setStep(2);
    } catch (e) {
      toast({ title: 'Error creando borrador', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  // ── Paso 2: subir PDF + analizar IA ──
  const uploadAndAnalyze = async () => {  // __V69_MULTI__
    const files = (pdfFiles && pdfFiles.length) ? pdfFiles : (pdfFile ? [pdfFile] : []);
    if (!files.length) { toast({ title: 'Selecciona al menos un PDF', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('tipo', 'resolucion');
        fd.append('file', f);
        await axios.post(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/adjuntos`, fd,
          { headers: headers({ 'Content-Type': 'multipart/form-data' }) });
      }
      toast({ title: `${files.length} PDF(s) subidos`, description: 'Analizando con IA…' });
      const r = await axios.post(
        `${BACKEND_URL}/api/ejecuciones/${ejecucionId}/analizar-resoluciones`, {},
        { headers: headers(), timeout: 300000 });
      setAnalysis(r.data);
      const a = r.data?.analisis || {};
      if (a.tipo_condena) setTipo(a.tipo_condena === 'mixta' ? 'mixta' : a.tipo_condena === 'hacer' ? 'hacer' : 'dineraria');
      if (a.hacer_personalisimo !== undefined && a.hacer_personalisimo !== null) setHacerPersonalisimo(a.hacer_personalisimo);
      const desc = a.objeto_ejecucion || a.fallo_resumen || '';
      if (desc) setObjeto(desc);
      const imp = a.importes || {};
      if (imp.principal != null) setImportePrincipal(String(imp.principal));
      if (imp.intereses_vencidos != null) setImporteIntereses(String(imp.intereses_vencidos));
      if (imp.costas_previstas != null) setImporteCostas(String(imp.costas_previstas));
      if (a.fecha_resolucion) setFechaResolucion(a.fecha_resolucion);
      if (a.fecha_firmeza) setFechaFirmeza(a.fecha_firmeza);
      const okN = r.data?.n_resoluciones_ok ?? files.length;
      const errN = (r.data?.errores || []).length;
      toast({ title: `Análisis IA completado · ${okN}/${files.length}`,
              description: errN ? `${errN} fichero(s) con error` : `Detectado: ${a.tipo_condena || 'n/d'}` });
      setStep(3);
    } catch (e) {
      toast({ title: 'Error análisis IA', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const skipIA = () => {
    toast({ title: 'IA omitida', description: 'Rellena los datos manualmente' });
    setStep(3);
  };

  // ── Paso 3: guardar definitivo ──
  const guardar = async () => {
    setBusy(true);
    try {
      const body = {
        tipo,
        hacer_personalisimo: (tipo === 'hacer' || tipo === 'mixta') ? hacerPersonalisimo : null,
        objeto_descripcion: objeto.trim() || null,
        importe_principal: parseFloat(importePrincipal) || 0,
        importe_intereses: parseFloat(importeIntereses) || 0,
        importe_costas: parseFloat(importeCostas) || 0,
        fecha_resolucion_ejecutar: fechaResolucion || null,
        fecha_firmeza: fechaFirmeza || null,
        notas: notas.trim() || null,
      };
      const r = await axios.put(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}`, body, { headers: headers() });
      onCreated?.(r.data);
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const cancelar = async () => {
    // Si se creó un borrador y se cancela antes de paso 3, borrarlo
    if (ejecucionId && step >= 2) {
      try { await axios.delete(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}`, { headers: headers() }); }
      catch (e) { /* ignore — el usuario podrá limpiarlo después */ }
    }
    onClose?.();
  };

  const TIPO_OPTS = [
    { value: 'dineraria', label: 'Dineraria', icon: Banknote, desc: 'Cobro de cantidad líquida (LEC 571-698)' },
    { value: 'hacer',     label: 'De hacer',  icon: Hammer,   desc: 'Cumplimiento de obligación de hacer (LEC 699-720)' },
    { value: 'mixta',     label: 'Mixta',     icon: Gavel,    desc: 'Combinada' },
  ];

  return (
    <Dialog open={open} onOpenChange={cancelar}>
      <DialogContent className="max-w-3xl" data-testid="dialog-nueva-ejecucion">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2"><Gavel className="w-5 h-5 text-violet-700" /> Nueva ejecución</span>
            <div className="flex items-center gap-1 text-xs">
              {[1,2,3].map(n => (
                <span key={n} className={`px-2.5 py-1 rounded-full ${step === n ? 'bg-violet-700 text-white' : step > n ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                  {n}. {n===1?'Expediente':n===2?'Resolución + IA':'Datos'}
                </span>
              ))}
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* ═════════ PASO 1: EXPEDIENTE ═════════ */}
        {step === 1 && (
          <div className="py-3 space-y-3">
            <p className="text-sm text-gray-600">Selecciona el expediente al que vincular la ejecución. En el siguiente paso subirás la resolución específica a ejecutar.</p>
            <div>
              <Label className="text-xs">Expediente <span className="text-red-600">*</span></Label>
              <Popover open={openExpCmb} onOpenChange={setOpenExpCmb}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox"
                          className={`w-full max-w-full justify-between font-normal overflow-hidden ${selectedExp ? 'border-green-500 bg-green-50 text-green-900 hover:bg-green-100' : ''}`}
                          data-testid="combobox-expediente-ejecucion">
                    {selectedExp ? (
                      <span className="truncate">
                        <b>{selectedExp.numero_expediente || selectedExp.numero || `#${selectedExp.id}`}</b>
                        {selectedExp.titulo ? ` · ${selectedExp.titulo}` : ''}
                      </span>
                    ) : <span className="text-gray-500">Selecciona un expediente…</span>}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[640px] max-w-[90vw] p-0" data-testid="popover-expedientes">  {/* __V71_CB_FIX__ */}
                  <Command shouldFilter={false}>
                    <CommandInput placeholder="Buscar por número o título…" value={expedienteQuery} onValueChange={setExpedienteQuery} />
                    <CommandEmpty>{loadingExp ? 'Buscando…' : 'Sin resultados'}</CommandEmpty>
                    <CommandGroup className="max-h-72 overflow-y-auto">
                      {expedientesOpts.map(ex => (
                        <CommandItem key={ex.id} value={String(ex.id)}
                                     onSelect={() => { setExpedienteId(ex.id); setSelectedExp(ex); setOpenExpCmb(false); }}
                                     className="items-start"
                                     data-testid={`opt-expediente-${ex.id}`}>
                          <Check className={`mr-2 mt-1 h-4 w-4 shrink-0 ${expedienteId === ex.id ? 'opacity-100' : 'opacity-0'}`} />
                          <div className="flex flex-col min-w-0 flex-1 gap-0.5">
                            <span className="font-mono text-xs truncate">{ex.numero_expediente || ex.numero || `#${ex.id}`}</span>
                            <span className="text-xs text-gray-600 break-words leading-snug">{ex.titulo || ''}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}

        {/* ═════════ PASO 2: RESOLUCIÓN + IA ═════════ */}
        {step === 2 && (
          <div className="py-3 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <b>Resolución específica a ejecutar.</b> Un expediente puede tener varias resoluciones — sube SÓLO la que vas a ejecutar (sentencia / auto / decreto firme). La IA analizará el fallo y rellenará automáticamente los datos.
            </div>

            <div>
              <Label className="text-xs">PDF de la resolución <span className="text-red-600">*</span></Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-violet-400 transition-colors">
                <input type="file" id="pdf-resolucion-input" hidden accept="application/pdf" multiple
                       onChange={(e) => setPdfFiles(Array.from(e.target.files || []))} />
                <label htmlFor="pdf-resolucion-input" className="cursor-pointer">
                  <FileText className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                  {pdfFiles && pdfFiles.length > 0 ? (
                    <div data-testid="pdf-files-list">
                      <div className="font-medium">{pdfFiles.length} archivo{pdfFiles.length>1?'s':''} seleccionado{pdfFiles.length>1?'s':''}</div>
                      <ul className="text-xs text-gray-600 mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                        {pdfFiles.map((f, i) => (<li key={i} className="truncate">• {f.name} <span className="text-gray-400">({Math.round(f.size/1024)} KB)</span></li>))}
                      </ul>
                      <div className="text-xs text-gray-500 mt-1">click para cambiar / añadir</div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">
                      <b>Click para seleccionar PDF</b> o arrastra aquí
                    </div>
                  )}
                </label>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={uploadAndAnalyze} disabled={(!pdfFiles || pdfFiles.length===0) || busy} className="flex-1 bg-violet-700 hover:bg-violet-800" data-testid="btn-analizar-ia">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Subiendo + analizando con IA…</> : <><Sparkles className="w-4 h-4 mr-2" />Analizar con IA</>}
              </Button>
              <Button variant="outline" onClick={skipIA} disabled={busy} data-testid="btn-skip-ia">
                Omitir IA
              </Button>
            </div>

            <div className="text-xs text-gray-500">
              ⓘ El análisis IA usa el modelo configurado en <i>Configuración → IA de Expedientes</i>. Puede tardar 10-40 segundos por PDF. Múltiples resoluciones se analizan en paralelo y los datos se fusionan.
            </div>
          </div>
        )}

        {/* ═════════ PASO 3: REVISIÓN + DATOS ═════════ */}
        {step === 3 && (
          <div className="py-3 space-y-3">
            {analysis && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <div className="flex items-center gap-2 font-semibold text-emerald-800 text-sm">
                  <Sparkles className="w-4 h-4" /> Análisis IA completado
                  <Badge variant="outline" className="ml-2 text-xs">{analysis.modelo_usado}</Badge>
                </div>
                {analysis.analisis?.fallo_resumen && (
                  <div className="text-xs text-emerald-900 mt-2 italic">"{analysis.analisis.fallo_resumen}"</div>
                )}
                {analysis.analisis?.advertencias?.length > 0 && (
                  <div className="mt-2 text-xs text-amber-800 flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <div>{analysis.analisis.advertencias.join(' · ')}</div>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs">Tipo de ejecución</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {TIPO_OPTS.map(t => {
                    const Icon = t.icon; const active = tipo === t.value;
                    return (
                      <div key={t.value} onClick={() => setTipo(t.value)}
                           className={`p-2 rounded-lg border-2 cursor-pointer text-center ${active ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:border-violet-300'}`}
                           data-testid={`tipo-${t.value}`}>
                        <Icon className="w-4 h-4 text-violet-700 mx-auto mb-1" />
                        <div className="text-xs font-medium">{t.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {(tipo === 'hacer' || tipo === 'mixta') && (
                <div className="md:col-span-2">
                  <Label className="text-xs">¿Personalísima?</Label>
                  <Select value={hacerPersonalisimo === null ? '' : String(hacerPersonalisimo)}
                          onValueChange={(v) => setHacerPersonalisimo(v === 'true')}>
                    <SelectTrigger data-testid="select-personalisimo"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="false">No · puede hacerla un tercero</SelectItem>
                      <SelectItem value="true">Sí · sólo el ejecutado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="md:col-span-2">
                <Label className="text-xs">Objeto / fallo a ejecutar</Label>
                <Textarea value={objeto} onChange={(e) => setObjeto(e.target.value)} rows={3} data-testid="input-objeto" />
              </div>

              {(tipo === 'dineraria' || tipo === 'mixta') && (
                <>
                  <div>
                    <Label className="text-xs">Principal (€)</Label>
                    <Input type="number" step="0.01" value={importePrincipal} onChange={(e) => setImportePrincipal(e.target.value)} data-testid="input-principal" />
                  </div>
                  <div>
                    <Label className="text-xs">Intereses (€)</Label>
                    <Input type="number" step="0.01" value={importeIntereses} onChange={(e) => setImporteIntereses(e.target.value)} data-testid="input-intereses" />
                  </div>
                  <div>
                    <Label className="text-xs">Costas (€)</Label>
                    <Input type="number" step="0.01" value={importeCostas} onChange={(e) => setImporteCostas(e.target.value)} data-testid="input-costas" />
                  </div>
                </>
              )}

              <div>
                <Label className="text-xs">Fecha resolución</Label>
                <Input type="date" value={fechaResolucion} onChange={(e) => setFechaResolucion(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Fecha firmeza</Label>
                <Input type="date" value={fechaFirmeza} onChange={(e) => setFechaFirmeza(e.target.value)} />
              </div>

              <div className="md:col-span-2">
                <Label className="text-xs">Notas internas</Label>
                <Textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={2} />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {step > 1 && step < 3 && (
            <Button variant="ghost" onClick={() => setStep(step - 1)} disabled={busy}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Atrás
            </Button>
          )}
          <Button variant="outline" onClick={cancelar} disabled={busy}>Cancelar</Button>
          {step === 1 && (
            <Button onClick={goStep2} disabled={!expedienteId || busy} className="bg-violet-700 hover:bg-violet-800" data-testid="btn-paso2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continuar <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          )}
          {step === 3 && (
            <Button onClick={guardar} disabled={busy} className="bg-violet-700 hover:bg-violet-800" data-testid="btn-guardar-ejecucion">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando…</> : <><Save className="w-4 h-4 mr-2" />Crear ejecución</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EjecucionForm;
